import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { driversService } from '../services/driversService';
import { Driver, UserRole } from '../types';
import { toast } from 'react-toastify';
import { RootState } from '../store/store';
import { DriversDetailModal } from '../components/DriversDetailModal';

const DriversPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterActive, setFilterActive] = useState<string>('all');

  // Detail modal state
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const canManageDrivers = user?.role === UserRole.MANAGER ||
                           user?.role === UserRole.ORG_ADMIN ||
                           user?.role === UserRole.PLATFORM_ADMIN ||
                           user?.role === UserRole.PROVIDER_MANAGER ||
                           user?.role === UserRole.PROVIDER_USER;

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const isActiveFilter =
        filterActive === 'all'
          ? undefined
          : filterActive === 'active';

      const data = await driversService.getAll(isActiveFilter);
      setDrivers(data);
    } catch (error: any) {
      console.error('Failed to load drivers:', error);
      toast.error(t('drivers.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterActive]);

  const handleRowClick = (driver: Driver) => {
    setSelectedDriver(driver);
    setIsDetailModalOpen(true);
  };

  const handleEdit = () => {
    if (selectedDriver) {
      navigate(`/app/drivers/${selectedDriver.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('drivers.title')}
          </h1>
          {canManageDrivers && (
            <button
              onClick={() => navigate('/app/drivers/new')}
              className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[44px] font-medium"
            >
              + {t('drivers.addDriver')}
            </button>
          )}
        </div>

        <div className="flex gap-4 mb-4 sm:mb-6">
          <div className="flex-1 sm:flex-none">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.status')}
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white min-h-[44px]"
            >
              <option value="all">{t('common.all')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {drivers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {t('drivers.noDrivers')}
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
            {t('drivers.driverHint')}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {drivers.map((driver) => (
              <li key={driver.id}>
                <div
                  onClick={() => handleRowClick(driver)}
                  className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          {driver.fullName}
                        </h3>
                        {!driver.isActive && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {t('common.inactive')}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="truncate">
                          <span className="font-medium">{t('drivers.phone')}:</span>{' '}
                          {driver.phone}
                        </div>
                        {driver.email && (
                          <div className="truncate">
                            <span className="font-medium">{t('common.email')}:</span>{' '}
                            {driver.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Detail Modal */}
      <DriversDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        driver={selectedDriver}
        onEdit={handleEdit}
        canManage={canManageDrivers}
      />
    </div>
  );
};

export default DriversPage;
